import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Car, Mail, Lock, User, Phone, Loader2, ArrowLeft, Truck, IdCard, MapPin, Calendar, Users, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { driverAPI } from '../lib/api';
import { toast } from 'sonner';

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
];

const DriverRegister = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // Multi-step form
  const [formData, setFormData] = useState({
    // Step 1: Basic Info
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    // Step 2: Vehicle Info
    vehicleType: '',
    vehiclePlate: '',
    // Step 3: Background Check Info
    driversLicense: '',
    licenseState: '',
    licenseExpiry: '',
    dateOfBirth: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    // Step 4: References
    reference1Name: '',
    reference1Phone: '',
    reference2Name: '',
    reference2Phone: '',
    backgroundCheckConsent: false,
  });

  const validateStep = (currentStep) => {
    if (currentStep === 1) {
      if (!formData.name || !formData.email || !formData.phone) {
        toast.error('Please fill in all required fields');
        return false;
      }
      if (formData.password !== formData.confirmPassword) {
        toast.error('Passwords do not match');
        return false;
      }
      if (formData.password.length < 6) {
        toast.error('Password must be at least 6 characters');
        return false;
      }
    }
    if (currentStep === 2) {
      if (!formData.vehicleType || !formData.vehiclePlate) {
        toast.error('Please fill in vehicle information');
        return false;
      }
    }
    if (currentStep === 3) {
      if (!formData.driversLicense || !formData.licenseState || !formData.licenseExpiry || 
          !formData.dateOfBirth || !formData.address || !formData.city || 
          !formData.state || !formData.zipCode) {
        toast.error('Please fill in all background check information');
        return false;
      }
    }
    if (currentStep === 4) {
      if (!formData.backgroundCheckConsent) {
        toast.error('You must consent to the background check to proceed');
        return false;
      }
    }
    return true;
  };

  const nextStep = () => {
    if (validateStep(step)) {
      setStep(step + 1);
    }
  };

  const prevStep = () => {
    setStep(step - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateStep(4)) return;

    setLoading(true);

    try {
      const response = await driverAPI.register({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        password: formData.password,
        vehicle_type: formData.vehicleType,
        vehicle_plate: formData.vehiclePlate,
        drivers_license: formData.driversLicense,
        license_state: formData.licenseState,
        license_expiry: formData.licenseExpiry,
        date_of_birth: formData.dateOfBirth,
        address: formData.address,
        city: formData.city,
        state: formData.state,
        zip_code: formData.zipCode,
        reference1_name: formData.reference1Name || null,
        reference1_phone: formData.reference1Phone || null,
        reference2_name: formData.reference2Name || null,
        reference2_phone: formData.reference2Phone || null,
        background_check_consent: formData.backgroundCheckConsent,
      });
      
      localStorage.setItem('token', response.data.access_token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      
      toast.success('Application submitted! Your account is pending approval.');
      navigate('/driver');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50 flex">
      {/* Left side - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md space-y-6">
          <div>
            <Link to="/" className="inline-flex items-center text-zinc-600 hover:text-zinc-900 mb-6 transition-colors">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to home
            </Link>
            
            <div className="flex items-center gap-2 mb-2">
              <div className="w-10 h-10 bg-orange-500 rounded-xl flex items-center justify-center">
                <Car className="w-6 h-6 text-white" />
              </div>
              <span className="font-bold text-2xl tracking-tight text-zinc-900" style={{ fontFamily: 'Manrope, sans-serif' }}>LocalMove</span>
            </div>
            
            <h1 className="text-3xl font-bold text-zinc-900 mt-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Become a Driver
            </h1>
            <p className="text-zinc-600 mt-2">
              Complete your application to start earning
            </p>

            {/* Progress Steps */}
            <div className="flex items-center justify-between mb-6">
              {[1, 2, 3, 4].map((s) => (
                <div key={s} className="flex items-center">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    step >= s ? 'bg-orange-500 text-white' : 'bg-zinc-200 text-zinc-500'
                  }`}>
                    {step > s ? <CheckCircle className="w-5 h-5" /> : s}
                  </div>
                  {s < 4 && <div className={`w-12 h-1 mx-1 ${step > s ? 'bg-orange-500' : 'bg-zinc-200'}`} />}
                </div>
              ))}
            </div>
            <p className="text-xs text-zinc-500 text-center mb-4">
              Step {step} of 4: {step === 1 ? 'Basic Info' : step === 2 ? 'Vehicle Info' : step === 3 ? 'Background Check' : 'References & Consent'}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Step 1: Basic Info */}
            {step === 1 && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-zinc-700">Full Name *</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400" />
                      <Input
                        id="name"
                        type="text"
                        placeholder="John Doe"
                        className="pl-10 h-11"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                        data-testid="driver-name-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-zinc-700">Phone *</Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400" />
                      <Input
                        id="phone"
                        type="tel"
                        placeholder="+1 (555) 000-0000"
                        className="pl-10 h-11"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        required
                        data-testid="driver-phone-input"
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-zinc-700">Email *</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@example.com"
                      className="pl-10 h-11"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      data-testid="driver-email-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="password" className="text-zinc-700">Password *</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400" />
                      <Input
                        id="password"
                        type="password"
                        placeholder="••••••••"
                        className="pl-10 h-11"
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        required
                        data-testid="driver-password-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword" className="text-zinc-700">Confirm *</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400" />
                      <Input
                        id="confirmPassword"
                        type="password"
                        placeholder="••••••••"
                        className="pl-10 h-11"
                        value={formData.confirmPassword}
                        onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                        required
                        data-testid="driver-confirm-password-input"
                      />
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* Step 2: Vehicle Info */}
            {step === 2 && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="vehicleType" className="text-zinc-700">Vehicle Type *</Label>
                  <Select 
                    value={formData.vehicleType}
                    onValueChange={(value) => setFormData({ ...formData, vehicleType: value })}
                  >
                    <SelectTrigger className="h-11" data-testid="driver-vehicle-type-select">
                      <SelectValue placeholder="Select vehicle type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="car">Car</SelectItem>
                      <SelectItem value="motorcycle">Motorcycle</SelectItem>
                      <SelectItem value="ebike">E-Bike</SelectItem>
                      <SelectItem value="bicycle">Bicycle</SelectItem>
                      <SelectItem value="van">Van</SelectItem>
                      <SelectItem value="pickup_truck">Pickup Truck</SelectItem>
                      <SelectItem value="box_truck">Box Truck</SelectItem>
                      <SelectItem value="flatbed">Flatbed</SelectItem>
                      <SelectItem value="drone">Drone</SelectItem>
                      <SelectItem value="robotaxi">Robotaxi</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="vehiclePlate" className="text-zinc-700">License Plate *</Label>
                  <div className="relative">
                    <Truck className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400" />
                    <Input
                      id="vehiclePlate"
                      type="text"
                      placeholder="ABC 1234"
                      className="pl-10 h-11"
                      value={formData.vehiclePlate}
                      onChange={(e) => setFormData({ ...formData, vehiclePlate: e.target.value })}
                      required
                      data-testid="driver-plate-input"
                    />
                  </div>
                </div>
              </>
            )}

            {/* Step 3: Background Check Info */}
            {step === 3 && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="driversLicense" className="text-zinc-700">Driver's License # *</Label>
                    <div className="relative">
                      <IdCard className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400" />
                      <Input
                        id="driversLicense"
                        type="text"
                        placeholder="D123-4567-8901"
                        className="pl-10 h-11"
                        value={formData.driversLicense}
                        onChange={(e) => setFormData({ ...formData, driversLicense: e.target.value })}
                        required
                        data-testid="driver-license-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="licenseState" className="text-zinc-700">License State *</Label>
                    <Select 
                      value={formData.licenseState}
                      onValueChange={(value) => setFormData({ ...formData, licenseState: value })}
                    >
                      <SelectTrigger className="h-11" data-testid="driver-license-state-select">
                        <SelectValue placeholder="Select state" />
                      </SelectTrigger>
                      <SelectContent>
                        {US_STATES.map((st) => (
                          <SelectItem key={st} value={st}>{st}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="licenseExpiry" className="text-zinc-700">License Expiry *</Label>
                    <div className="relative">
                      <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400" />
                      <Input
                        id="licenseExpiry"
                        type="date"
                        className="pl-10 h-11"
                        value={formData.licenseExpiry}
                        onChange={(e) => setFormData({ ...formData, licenseExpiry: e.target.value })}
                        required
                        data-testid="driver-license-expiry-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="dateOfBirth" className="text-zinc-700">Date of Birth *</Label>
                    <div className="relative">
                      <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400" />
                      <Input
                        id="dateOfBirth"
                        type="date"
                        className="pl-10 h-11"
                        value={formData.dateOfBirth}
                        onChange={(e) => setFormData({ ...formData, dateOfBirth: e.target.value })}
                        required
                        data-testid="driver-dob-input"
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="address" className="text-zinc-700">Street Address *</Label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400" />
                    <Input
                      id="address"
                      type="text"
                      placeholder="123 Main Street"
                      className="pl-10 h-11"
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      required
                      data-testid="driver-address-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="city" className="text-zinc-700">City *</Label>
                    <Input
                      id="city"
                      type="text"
                      placeholder="Mascoutah"
                      className="h-11"
                      value={formData.city}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                      required
                      data-testid="driver-city-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="state" className="text-zinc-700">State *</Label>
                    <Select 
                      value={formData.state}
                      onValueChange={(value) => setFormData({ ...formData, state: value })}
                    >
                      <SelectTrigger className="h-11" data-testid="driver-state-select">
                        <SelectValue placeholder="State" />
                      </SelectTrigger>
                      <SelectContent>
                        {US_STATES.map((st) => (
                          <SelectItem key={st} value={st}>{st}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="zipCode" className="text-zinc-700">ZIP *</Label>
                    <Input
                      id="zipCode"
                      type="text"
                      placeholder="62258"
                      className="h-11"
                      value={formData.zipCode}
                      onChange={(e) => setFormData({ ...formData, zipCode: e.target.value })}
                      required
                      data-testid="driver-zip-input"
                    />
                  </div>
                </div>
              </>
            )}

            {/* Step 4: References & Consent */}
            {step === 4 && (
              <>
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
                  <h3 className="font-semibold text-orange-800 flex items-center gap-2">
                    <Users className="w-5 h-5" />
                    References (Optional)
                  </h3>
                  <p className="text-sm text-orange-700 mt-1">
                    Providing references can help expedite your approval process.
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="reference1Name" className="text-zinc-700">Reference 1 Name</Label>
                    <Input
                      id="reference1Name"
                      type="text"
                      placeholder="Jane Smith"
                      className="h-11"
                      value={formData.reference1Name}
                      onChange={(e) => setFormData({ ...formData, reference1Name: e.target.value })}
                      data-testid="driver-ref1-name-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="reference1Phone" className="text-zinc-700">Reference 1 Phone</Label>
                    <Input
                      id="reference1Phone"
                      type="tel"
                      placeholder="+1 (555) 000-0000"
                      className="h-11"
                      value={formData.reference1Phone}
                      onChange={(e) => setFormData({ ...formData, reference1Phone: e.target.value })}
                      data-testid="driver-ref1-phone-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="reference2Name" className="text-zinc-700">Reference 2 Name</Label>
                    <Input
                      id="reference2Name"
                      type="text"
                      placeholder="Bob Johnson"
                      className="h-11"
                      value={formData.reference2Name}
                      onChange={(e) => setFormData({ ...formData, reference2Name: e.target.value })}
                      data-testid="driver-ref2-name-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="reference2Phone" className="text-zinc-700">Reference 2 Phone</Label>
                    <Input
                      id="reference2Phone"
                      type="tel"
                      placeholder="+1 (555) 000-0000"
                      className="h-11"
                      value={formData.reference2Phone}
                      onChange={(e) => setFormData({ ...formData, reference2Phone: e.target.value })}
                      data-testid="driver-ref2-phone-input"
                    />
                  </div>
                </div>

                <div className="bg-zinc-100 rounded-lg p-4 mt-4">
                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="backgroundCheckConsent"
                      checked={formData.backgroundCheckConsent}
                      onCheckedChange={(checked) => setFormData({ ...formData, backgroundCheckConsent: checked })}
                      data-testid="driver-consent-checkbox"
                    />
                    <div>
                      <Label htmlFor="backgroundCheckConsent" className="text-zinc-700 font-medium cursor-pointer">
                        Background Check Consent *
                      </Label>
                      <p className="text-xs text-zinc-500 mt-1">
                        I consent to LocalMove conducting a background check and verifying the information I have provided. 
                        I understand my application will be reviewed and I will be notified of the decision.
                      </p>
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* Navigation Buttons */}
            <div className="flex gap-3 pt-2">
              {step > 1 && (
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1 h-12 rounded-xl"
                  onClick={prevStep}
                  data-testid="driver-register-prev-btn"
                >
                  Back
                </Button>
              )}
              
              {step < 4 ? (
                <Button
                  type="button"
                  className="flex-1 h-12 bg-orange-500 hover:bg-orange-600 text-white rounded-xl"
                  onClick={nextStep}
                  data-testid="driver-register-next-btn"
                >
                  Continue
                </Button>
              ) : (
                <Button 
                  type="submit" 
                  className="flex-1 h-12 bg-orange-500 hover:bg-orange-600 text-white rounded-xl text-base font-medium"
                  disabled={loading}
                  data-testid="driver-register-submit-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    'Submit Application'
                  )}
                </Button>
              )}
            </div>
          </form>

          <div className="text-center">
            <p className="text-zinc-600">
              Already a driver?{' '}
              <Link to="/login" className="text-orange-500 hover:text-orange-600 font-medium">
                Log in
              </Link>
            </p>
          </div>
        </div>
      </div>

      {/* Right side - Image */}
      <div className="hidden lg:block lg:flex-1 relative">
        <img
          src="https://images.unsplash.com/photo-1659353739926-4c7df1a645a6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwyfHxkZWxpdmVyeSUyMGRyaXZlciUyMHNtaWxpbmclMjBib3glMjBkb29yc3RlcCUyMGZyaWVuZGx5fGVufDB8fHx8MTc3MTE0MjgzNHww&ixlib=rb-4.1.0&q=85"
          alt="Friendly local driver"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <h2 className="text-3xl font-bold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Earn on your schedule
          </h2>
          <p className="text-white/80 text-lg">
            Set your own hours and earn competitive rates serving your local community.
          </p>
        </div>
      </div>
    </div>
  );
};

export default DriverRegister;
